import { faArrowsRotate, faFlag, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import _map from 'lodash/map';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import Alert from 'src/components/ui/Alert';
import Checkbox from 'src/components/ui/Checkbox';
import Pagination from 'src/components/ui/Pagination';
import useActionPanelStore from 'src/stores/useActionPanelStore';
import { api, getAllByFilter } from 'src/utils/api';
import {
  API_RESOURCES,
  PAGINATION_IGNORE_DEFAULT_LIMIT,
  PRINT_STATUSES,
  PRODUCTION_CHANGE,
  PRODUCTION_CHANGE_LABELS,
  RUN_ACTIONS,
} from 'src/utils/constants';
import { getShortUuid } from 'src/utils/url';
import userPropType from 'src/utils/user-prop-type';

import NcrModal from '../sections/_ncr-modal';
import RemanufactureModal from '../sections/_remanfacture-modal';
import ScrapModal from '../sections/_scrap-modal';
import RunActionPageWrapper from './_action-wrapper';

const ProductionChange = ({ user }) => {
  const { uuid: runUUID } = useParams();
  const { addToast } = useToasts();
  const { openActionPanel, closeActionPanel } = useActionPanelStore();

  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingPrints, setIsLoadingPrints] = useState(false);

  const [run, setRun] = useState(null);
  const [pieces, setPieces] = useState([]);
  const [totalPieces, setTotalPieces] = useState(0);
  const [postProcessor, setPostProcessor] = useState(null);
  const [printsByPiece, setPrintsByPiece] = useState({});
  const [runTransformations, setRunTransformations] = useState([]);

  const [selectedPieces, setSelectedPieces] = useState([]);
  const [selectedAction, setSelectedAction] = useState(null);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const totalPages = Math.ceil(totalPieces / pageSize);

  const getNcrPostProcessor = async (run) => {
    try {
      const { resources }  = await api.get(`${API_RESOURCES.POST_PROCESSOR}/`, {
        searchParams: {
          'filter[location]': run.location,
          'filter[post_processor_special_type]': 'non_conformance_review',
        },
      }).json();

      setPostProcessor(resources[0]);
    } catch (error) {
      console.error(error); 
      addToast('Failed to load post processor.', { appearance: 'error' });
    }
  }

  const getPrints = async () => {
    const pieceUris = [...new Set(pieces.map(p => p.piece))];
    if (!pieceUris.length) return;

    try {
      setIsLoadingPrints(true);

      const { resources: prints }  = await api.get(`${API_RESOURCES.PRINT}/`, {
        searchParams: {
          'filter[piece]': pieceUris.join(','),
          'page[limit]': PAGINATION_IGNORE_DEFAULT_LIMIT,
        },
      }).json();

      const groupedByPiece = prints.reduce((acc, item) => (
        (acc[item.piece] ||= []).push(item), acc
      ), {});

      Object.values(groupedByPiece).forEach(group => {
        group.sort((a, b) => a.process_step_position - b.process_step_position);
      });

      setPrintsByPiece(groupedByPiece);
    } catch (error) {
      console.error(error);       
      addToast('Failed to load prints.', { appearance: 'error' });
    } finally {
      setIsLoadingPrints(false);
    }
  };

  const getPieces = async () => {
    try {
      const offset = (currentPage - 1) * pageSize;

      const { resources: pieces, meta }  = await api.get(`${API_RESOURCES.PIECES_BY_RUN}/`, {
        searchParams: {
          'filter[run]': run.uri,
          'page[limit]': pageSize,
          'page[offset]': offset,
        },
      }).json();

      setPieces(pieces);
      setTotalPieces(meta.count);
    } catch (error) {
      console.error(error);       
      addToast('Failed to load pieces.', { appearance: 'error' });
    }
  };

  const getSelectedPrints = async () =>
    getAllByFilter(
      // copy — chunkForQueryFilter mutates its input array, but we reuse this array in UI
      [...selectedPieces],
      `${API_RESOURCES.PRINT}/`,
      { searchParams: { 'filter[run]': run.uri } },
      // filter key → filter[piece], chunked at 15 per request
      'piece',
    );

  const getRunTransformations = async (run) => {
    const { resources: runTransformations }  = await api.get(`${API_RESOURCES.RUN_TRANSFORMATION}/`, {
      searchParams: {
        'filter[source_run]': run.uri,
        'page[limit]': PAGINATION_IGNORE_DEFAULT_LIMIT,
      },
    }).json();

    setRunTransformations(runTransformations);
  }

  const getInitialData = async () => {
    if (!runUUID) {
      setIsLoading(false);
      return;
    }
  
    try {
      setIsLoading(true);
      const currentRun = await api.get(`${API_RESOURCES.RUN}/${runUUID}/`).json();

      if (!currentRun) {
        setIsLoading(false);
        return;
      }

      setRun(currentRun);
      getRunTransformations(currentRun);
      getNcrPostProcessor(currentRun);
    } catch (error) {
      if (error.name === 'HTTPError' && error.response.status === 404) {
        setError('Failed to find the run.');
      } else {
        setError(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const isNcrAvailable = Boolean(postProcessor);

  const ncrLockedActions = !isNcrAvailable &&
    selectedAction === PRODUCTION_CHANGE.FLAG_NON_CONFORMANCE;

  const isPieceBaseDisabled = (data) => {
    const pieceState = pieceStates[data.piece];

    const hasTransformation = runTransformations.some(
      // eslint-disable-next-line camelcase
      ({ related_pieces }) => related_pieces?.includes(data.piece)
    );

    const isBlockedByTransformation =
      hasTransformation &&
      ![
        PRODUCTION_CHANGE.SCRAP,
        PRODUCTION_CHANGE.FLAG_NON_CONFORMANCE,
      ].includes(selectedAction);

    return (
      !selectedAction ||
      ncrLockedActions ||
      !pieceState?.isEligible ||
      isBlockedByTransformation
    );
  };

  const isPieceDisabled = (data) => {
    if (isLoadingPrints && selectedAction) return false;

    return (
      isPieceBaseDisabled(data) ||
      (
        workflowLockedActions &&
        selectedWorkflow &&
        data.workflow !== selectedWorkflow
      )
    )
  }

  const handleSelectAllPieces = ({ target: { checked } }) => {
    const selectablePieces = pieces.filter(piece => !isPieceDisabled(piece));
    const selectableIds = selectablePieces.map(p => p.piece);

    let nextSelected;

    if (checked) {
      nextSelected = [...new Set([...selectedPieces, ...selectableIds])];
      if (!selectedWorkflow && selectablePieces.length) {
        setSelectedWorkflow(selectablePieces[0].workflow);
      }
    } else {
      const selectableSet = new Set(selectableIds);
      nextSelected = selectedPieces.filter(id => !selectableSet.has(id));

      if (!nextSelected.length) {
        setSelectedWorkflow(null);
      }
    }

    setSelectedPieces(nextSelected);
  };

  const handleSelectPiece = (pieceUri) => {
    const piece = pieces.find(data => data.piece === pieceUri);

    setSelectedPieces(prev => {
      const isDeselecting = prev.includes(pieceUri);

      const nextSelected = isDeselecting
        ? prev.filter(uri => uri !== pieceUri)
        : [...prev, pieceUri];

      if (nextSelected.length === 0) {
        setSelectedWorkflow(null);
      } else if (!isDeselecting) {
        setSelectedWorkflow(piece.workflow);
      }

      return nextSelected;
    });
  };

  const handleReset = () => {
    setSelectedPieces([]);
    setSelectedWorkflow(null);
  }

  const handleRunTransformation = async ({
    type,
    notes,
    reason,
    splitSchedules,
    successMessage,
    errorMessage,
  }) => {
    try {
      const prints = await getSelectedPrints();

      const selectedPrints = prints.filter(
        print => selectedPieces.includes(print.piece)
      );

      const formattedNotes =
        type === 'remanufacture' && (reason || notes)
          ? reason && notes
            ? `${reason}: ${notes}`
            : reason || notes
          : notes;

      const payload = {
        type,
        prints: _map(selectedPrints, 'uri'),
        // eslint-disable-next-line camelcase
        source_run: run.uri,
        notes: formattedNotes,
        ...(splitSchedules && {
          // eslint-disable-next-line camelcase
          split_piece_schedules: splitSchedules,
        }),
      };

      await api.post(`${API_RESOURCES.RUN_TRANSFORMATION}/`, {
        json: payload,
      }).json();

      await getPieces();
      await getRunTransformations(run);

      closeActionPanel();
      setSelectedPieces([]);
      setSelectedWorkflow(null);

      addToast(successMessage, { appearance: 'success' });
    } catch (error) {
      console.error(error);
      const response = await error.response?.json?.();

      addToast(
        response?.errors?.[0]?.title || errorMessage,
        { appearance: 'error' }
      );
    }
  };

  const runPrintSet = useMemo(
    () => new Set(run?.prints ?? []),
    [run?.prints]
  );

  const pieceStates = useMemo(() => 
    Object.fromEntries(
      pieces.map(piece => {
        const prints = printsByPiece[piece.piece] ?? [];

        const current = prints.find(p => runPrintSet.has(p.uri));
        const next = current
          ? prints[prints.indexOf(current) + 1]
          : prints[0];

        const isCurrentComplete = current?.status === PRINT_STATUSES.complete;
        const isNextReady = [
          PRINT_STATUSES.queued,
          PRINT_STATUSES.queuedReady,
          PRINT_STATUSES.created,
        ].includes(next?.status);

        return [
          piece.piece,
          {
            current,
            next,
            isCurrentComplete,
            isNextReady,
            isEligible: isCurrentComplete && isNextReady,
          },
        ];
      })
    ),
    [pieces, printsByPiece, runPrintSet]
  );

  const hasDisabledPieces = Object.values(pieceStates)
    .some(state => !state.isEligible);

  const handleScrapPieces = (notes) =>
    handleRunTransformation({
      type: PRODUCTION_CHANGE.SCRAP,
      notes,
      successMessage: 'Selected pieces were successfully scrapped.',
      errorMessage: 'Selected pieces could not be scrapped.',
    });

  const handleRemanufacturePieces = ({ reason, notes }) =>
    handleRunTransformation({
      type: PRODUCTION_CHANGE.REMANUFACTURE,
      reason,
      notes,
      successMessage: 'Selected pieces were successfully remanufactured.',
      errorMessage: 'Selected pieces could not be remanufactured.',
    });

  const handleFlagForNcrPieces = ({ notes, splitSchedules }) =>
    handleRunTransformation({
      type: PRODUCTION_CHANGE.FLAG_NON_CONFORMANCE,
      notes,
      splitSchedules,
      successMessage: 'Selected pieces were successfully sent for Non-conformance Review.',
      errorMessage: 'Selected pieces could not be sent for Non-conformance Review.',
    });

  const handleBeginAction = () => {
    if (selectedAction === PRODUCTION_CHANGE.REMANUFACTURE) {
      openActionPanel({
        panelId: 'remanufacture-pieces-panel',
        title: 'Confirm Remanufacture',
        content: (
          <RemanufactureModal
            selectedPieces={selectedPieces}
            onConfirm={handleRemanufacturePieces}
            onDismiss={() => {}}
          />
        ),
      });

      return;
    }

    if (selectedAction === PRODUCTION_CHANGE.SCRAP) {
      openActionPanel({
        panelId: 'scrap-pieces-panel',
        title: 'Confirm Scrap',
        content: (
          <ScrapModal
            selectedPieces={selectedPieces}
            onConfirm={handleScrapPieces}
            onDismiss={() => {}}
            onCancelAndRemanufacture={() => {
              closeActionPanel();
            }}
          />
        ),
      });
    }

    if (selectedAction === PRODUCTION_CHANGE.FLAG_NON_CONFORMANCE) {
      openActionPanel({
        panelId: 'ncr-pieces-panel',
        title: 'Confirm Non-conformance Review',
        content: (
          <NcrModal
            selectedPieces={selectedPieces}
            onConfirm={handleFlagForNcrPieces}
            onDismiss={() => {}}
          />
        ),
      });
    }
  };

  useEffect(() => {
    getInitialData();
  }, []);

  useEffect(() => {
    if (run) {
      getPieces();
    }
  }, [run, currentPage, pageSize]);

    useEffect(() => {
    if (pieces.length > 0) {
      getPrints();
    }
  }, [pieces]);

  const workflowLockedActions = selectedAction === PRODUCTION_CHANGE.REMANUFACTURE ||
    selectedAction === PRODUCTION_CHANGE.FLAG_NON_CONFORMANCE

  const selectableWorkflows = [
    ...new Set(
      pieces
        .filter(piece => !isPieceBaseDisabled(piece))
        .map(piece => piece.workflow)
    ),
  ];

  const hasWorkflowMismatchOnPage =
    workflowLockedActions &&
    selectedWorkflow &&
    pieces.some(piece =>
      !isPieceBaseDisabled(piece) &&
      piece.workflow !== selectedWorkflow
    );

  const hasSelectableWorkflowMismatch =
    workflowLockedActions &&
    selectableWorkflows.length > 1;

  return (
    <RunActionPageWrapper
      id={runUUID}
      user={user}
      httpError={error}
      action={RUN_ACTIONS.PRODUCTION_CHANGE}
      actionTarget={run?.name}
      isLoading={isLoading}
      loadingText="Production Change"
    >
      <div className="d-flex flex-column w-100">
        <div className="card-base w-100 p-3">
          <div className="fw-semibold text-left text-uppercase mb-2">
            Select your action
          </div>
          {!isLoadingPrints && hasDisabledPieces && (
            <Alert
              variant="warning"
              name="latest-run-warning"
              icon={faTriangleExclamation}
              iconClassName="mt-1"
              className="d-flex align-items-start gap-10 mt-0"
            >
              Only pieces that are currently at the latest completed step in their workflow can be selected for production change.
            </Alert>
          )}
          <div
            className="d-flex flex-wrap gap-10"
            role="radiogroup"
            aria-label="Production change actions"
          >
            <button
              type="button"
              role="radio"
              aria-checked={selectedAction === PRODUCTION_CHANGE.REMANUFACTURE}
              className={`
                button button--primary button--sm d-flex gap-10 mw-100
                ${selectedAction === PRODUCTION_CHANGE.REMANUFACTURE ? 'button--selected' : ''}
              `}
              onClick={() => {
                setSelectedAction(PRODUCTION_CHANGE.REMANUFACTURE);
                handleReset();
              }}
            >
              <FontAwesomeIcon
                className="button__icon"
                icon={faArrowsRotate}
              />
              Remanufacture
            </button>

            <button
              type="button"
              role="radio"
              aria-checked={selectedAction === PRODUCTION_CHANGE.FLAG_NON_CONFORMANCE}
              className={`
                button button--primary button--sm d-flex gap-10 mw-100
                ${selectedAction === PRODUCTION_CHANGE.FLAG_NON_CONFORMANCE ? 'button--selected' : ''}
              `}
              onClick={() => {
                setSelectedAction(PRODUCTION_CHANGE.FLAG_NON_CONFORMANCE);
                handleReset();
              }}
            >
              <FontAwesomeIcon
                className="button__icon"
                icon={faFlag}
              />
              Flag for NCR
            </button>

            <button
              type="button"
              role="radio"
              aria-checked={selectedAction === PRODUCTION_CHANGE.SCRAP}
              className={`
                button button--danger button--sm d-flex gap-10 mw-100
                ${selectedAction === PRODUCTION_CHANGE.SCRAP ? 'button--selected' : ''}
              `}
              onClick={() => {
                setSelectedAction(PRODUCTION_CHANGE.SCRAP);
                handleReset();
              }}
            >
              <FontAwesomeIcon
                className="button__icon"
                icon={faTrashCan}
              />
              Scrap
            </button>
          </div>
        </div>
        <div className="card-base w-100 p-3">
          <div className="fw-semibold text-left text-uppercase mb-2">
            Select Pieces {selectedAction && `for ${PRODUCTION_CHANGE_LABELS[selectedAction]}`}
          </div>
          {hasWorkflowMismatchOnPage && (
            <Alert
              variant="warning"
              name="remanufacture-warning"
              icon={faTriangleExclamation}
              iconClassName="mt-1"
              className="d-flex align-items-start gap-10"
            >
              Production changes can only be initiated for pieces with the same workflow.
            </Alert>
          )}
          {ncrLockedActions && (
            <Alert
              variant="warning"
              name="ncr-warning"
              icon={faTriangleExclamation}
              iconClassName="mt-1"
              className="d-flex align-items-start gap-10"
            >
              Non-conformance Review Post Processor does not exist at the current location,
              so you are unable to select any pieces for this action.
            </Alert>
          )}
          <div className="d-flex flex-column text-left">
            <div className="d-flex flex-wrap justify-content-between gap-10 mb-4">
              <Checkbox
                id="selectAll"
                label={
                  <span className='fw-semibold'>
                    Select all available (for page {currentPage})
                  </span>
                }
                variant="highlight"
                checked={
                  pieces.some(piece => !isPieceDisabled(piece)) &&
                  pieces
                    .filter(piece => !isPieceDisabled(piece))
                    .every(({ piece }) => selectedPieces.includes(piece))
                }
                disabled={
                  !selectedAction ||
                  hasWorkflowMismatchOnPage ||
                  hasSelectableWorkflowMismatch ||
                  ncrLockedActions
                }
                onChange={handleSelectAllPieces}
              />
              <button
                type="button"
                className="button button--square button--sm mw-100 d-flex gap-10"
                disabled={
                  !selectedAction ||
                  !selectedPieces.length
                }
                onClick={handleReset}
              >
                Clear Selected Pieces ({selectedPieces.length})
              </button>
            </div>
            <div>
              {pieces.length > 0 && pieces.map((data, index) => {
                const pieceState = pieceStates[data.piece];
                const hasPrintsByPiece = Object.keys(printsByPiece).length > 0;

                const hasTransformation = runTransformations.some(
                  // eslint-disable-next-line camelcase
                  ({ related_pieces }) => related_pieces?.includes(data.piece)
                );

                const isEligible = pieceState?.isEligible;
                const isDisabled = isPieceDisabled(data);

                const rowClassName = 
                  index === pieces.length - 1 ? 
                    'd-flex justify-content-between align-items-center flex-wrap gap-10' :
                    'd-flex justify-content-between align-items-center flex-wrap gap-10 border-bottom pb-2 mb-2';

                const showRemanufacturedBadge =
                  !isLoadingPrints && hasPrintsByPiece && hasTransformation;

                const showNotEligibleBadge =
                  !isLoadingPrints  && hasPrintsByPiece && !isEligible && !hasTransformation;
                  
                return (
                  <div key={data.piece} className={rowClassName}>
                    <Checkbox
                      id={`piece-${data.piece}`}
                      label={
                        <div className='d-flex flex-column'>
                          <span className='fw-semibold'>{data.piece_name}</span>
                          <span className="small">{getShortUuid(data.piece)}</span>
                        </div>
                      }
                      checked={selectedPieces.includes(data.piece)}
                      disabled={isDisabled}
                      onChange={() => handleSelectPiece(data.piece)}
                    />
                    {showRemanufacturedBadge && (
                      <span className="production-change__badge production-change__badge--info">
                        Remanufactured
                      </span>
                    )}
                    {showNotEligibleBadge && (
                      <span className="production-change__badge production-change__badge--locked">
                        Not Eligible
                      </span>
                    )}
                  </div>
                )
              })}
            </div>

            <Pagination
              className="mt-4"
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={handlePageSizeChange}
            />
          </div>
        </div>
        <div className="d-flex justify-content-center">
          <button
            type="button"
            className="button button--square button--fill button--full button--primary d-flex gap-10"
            disabled={
              !selectedAction ||
              !selectedPieces.length
            }
            onClick={handleBeginAction}
          >
            Begin
          </button>
        </div>
      </div>
    </RunActionPageWrapper>
  )
}

ProductionChange.propTypes = {
  user: userPropType,
};

export default ProductionChange;
